/**
 * HuntingResultsView — analyst-facing display for an Advanced Hunting
 * KQL result set.
 *
 * Replaces the inline-table-only rendering that previously lived
 * duplicated across IncidentDetail + IncidentInvestigation. Adds:
 *
 *   - Inline compact table (first 50 rows, all projected columns)
 *   - Click any row → row-detail modal showing every projected field as
 *     a key/value list. Useful for cells that get truncated in the table
 *     (long URLs, multi-line process command lines, embedded JSON, etc.)
 *   - Empty-state handling for both no-results and missing-schema cases
 *
 * Cell values run through formatHuntingCellValue so ISO timestamps,
 * objects, and nulls render readably.
 *
 * A full-screen "Expand" modal was prototyped here (commit c140e43) but
 * pulled out — the `max-w-[95vw]` Modal size rendered as a grey frame
 * with no content visible. The investigation-page rework will be the
 * proper vehicle for giving result tables more space; until then the
 * inline view + row-click detail is what we ship.
 */
import { useState } from 'react';
import type { HuntingQueryResult } from '../../data/types';
import { formatHuntingCellValue } from '../../lib/huntingFormat';
import { Modal } from '../ui/Modal';

interface HuntingResultsViewProps {
  result: HuntingQueryResult;
}

const INLINE_ROW_LIMIT = 50;
const CELL_MAX_WIDTH = 'max-w-[280px]';

/**
 * Colour the outcome words the sign-in presets project as `Result` (AB#131)
 * so success/failure reads at a glance without hunting for the error code.
 */
function outcomeTone(display: string): string {
  if (display === 'Success') return 'text-green-600 dark:text-green-400 font-medium';
  if (display === 'Failure') return 'text-red-600 dark:text-red-400 font-medium';
  return '';
}

export function HuntingResultsView({ result }: HuntingResultsViewProps) {
  const [selectedRow, setSelectedRow] = useState<{
    row: Record<string, unknown>;
    index: number;
  } | null>(null);

  const hasRows = result.results.length > 0;
  const hasColumns = result.columns.length > 0;

  return (
    <div className="space-y-3">
      {/* Header: query name + stats */}
      <div className="flex items-center justify-between gap-3">
        <div className="min-w-0">
          <p className="font-medium text-text-primary truncate">{result.queryName}</p>
          {result.queryDescription && (
            <p className="text-xs text-text-secondary">{result.queryDescription}</p>
          )}
        </div>
        <span className="text-sm text-text-secondary whitespace-nowrap shrink-0">
          {result.resultCount} result{result.resultCount === 1 ? '' : 's'} • {result.executionTimeMs}ms
        </span>
      </div>

      {/* Inline body */}
      {!hasRows ? (
        <div className="p-6 bg-bg-tertiary rounded-lg text-center">
          <p className="text-text-primary text-sm font-medium">Query ran successfully — no matching rows</p>
          <p className="text-xs text-text-secondary mt-1">
            Advanced Hunting returned 0 results for this query window.
          </p>
        </div>
      ) : !hasColumns ? (
        <div className="p-6 bg-bg-tertiary rounded-lg text-center">
          <p className="text-text-secondary">
            {result.resultCount} result{result.resultCount === 1 ? '' : 's'} returned but no column metadata —
            check the analyst-api logs for schema-parsing issues.
          </p>
        </div>
      ) : (
        <HuntingTable
          columns={result.columns}
          rows={result.results}
          rowLimit={INLINE_ROW_LIMIT}
          onRowClick={(row, index) => setSelectedRow({ row, index })}
        />
      )}

      {/* Row detail modal — projected fields as key/value */}
      {selectedRow && (
        <Modal
          isOpen={!!selectedRow}
          onClose={() => setSelectedRow(null)}
          title={`Row ${selectedRow.index + 1} of ${result.resultCount}`}
          description={result.queryName}
          size="2xl"
        >
          <HuntingRowDetail row={selectedRow.row} columns={result.columns} />
        </Modal>
      )}
    </div>
  );
}

// ============================================================================
// Internals
// ============================================================================

interface HuntingTableProps {
  columns: string[];
  rows: Array<Record<string, unknown>>;
  /** Optional cap on rows rendered; if omitted, all rows are rendered. */
  rowLimit?: number;
  onRowClick: (row: Record<string, unknown>, index: number) => void;
}

function HuntingTable({ columns, rows, rowLimit, onRowClick }: HuntingTableProps) {
  const visibleRows = rowLimit ? rows.slice(0, rowLimit) : rows;
  const truncated = rowLimit ? rows.length > rowLimit : false;

  return (
    <div className="overflow-x-auto overflow-y-hidden border border-border-default rounded-lg">
      <table className="text-sm" style={{ minWidth: '100%' }}>
        <thead>
          <tr className="bg-bg-tertiary">
            {columns.map((col) => (
              <th
                key={col}
                className="text-left py-2 px-3 font-medium text-text-secondary text-xs border-b border-border-default whitespace-nowrap"
              >
                {col}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {visibleRows.map((row, idx) => (
            <tr
              key={idx}
              onClick={() => onRowClick(row, idx)}
              className="hover:bg-accent-blue/5 cursor-pointer transition-colors"
              title="Click to see full row detail"
            >
              {columns.map((col) => {
                const display = formatHuntingCellValue(row[col]);
                return (
                  <td
                    key={col}
                    className="py-2 px-3 text-text-primary border-b border-border-light align-top"
                  >
                    <span
                      className={`block truncate ${CELL_MAX_WIDTH} ${outcomeTone(display)}`}
                      title={display}
                    >
                      {display === '' ? '-' : display}
                    </span>
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
      {truncated && (
        <div className="p-2 text-center text-xs text-text-secondary bg-bg-tertiary">
          Showing {visibleRows.length} of {rows.length} rows
        </div>
      )}
    </div>
  );
}

interface HuntingRowDetailProps {
  row: Record<string, unknown>;
  columns: string[];
}

function HuntingRowDetail({ row, columns }: HuntingRowDetailProps) {
  // Iterate columns (not Object.keys(row)) so display order matches the
  // KQL `project` order, which is also the order the analyst sees in the
  // table — predictable cross-reference.
  return (
    <dl className="divide-y divide-border-light">
      {columns.map((col) => {
        const raw = row[col];
        const isNullish = raw === null || raw === undefined;
        const isObject = !isNullish && typeof raw === 'object';
        // Render strings, numbers, booleans as their natural form (using
        // the format helper so ISO timestamps come out readable). Objects
        // and arrays get pretty-printed JSON for inspection.
        const display = isObject ? JSON.stringify(raw, null, 2) : formatHuntingCellValue(raw);

        return (
          <div key={col} className="grid grid-cols-3 gap-4 py-2.5">
            <dt className="text-xs font-medium text-text-secondary uppercase tracking-wide pt-0.5">
              {col}
            </dt>
            <dd className="col-span-2 text-sm text-text-primary min-w-0">
              {isNullish ? (
                <span className="text-text-secondary italic">null</span>
              ) : isObject ? (
                <pre className="font-mono text-xs bg-bg-tertiary p-2 rounded whitespace-pre-wrap break-all">
                  {display}
                </pre>
              ) : (
                <span className="block break-words">{display}</span>
              )}
            </dd>
          </div>
        );
      })}
    </dl>
  );
}

